import { useCallback, useEffect, useState } from 'react';
import { getSDK } from '@/store/sdkStore';
import { Button } from '@/components/ui/button';
import { friendlyError, logErrorToJournal } from '@/lib/errors';

// ============================================================================
// FillRunner — раннер прохождения опроса/теста (§19).
// Один вопрос на экран + прогресс-бар + авто-переход для вариантов.
//
// ЯВНЫЕ состояния загрузки вопросов (загрузка / ошибка / пусто / готово):
// «вечной загрузки» не бывает — ошибка и ПУСТОЙ список вопросов показываются
// текстом (внешние методики psy: контент по лицензии не включён, прохождение
// недоступно — результат вносится вручную в протокол).
// ============================================================================

export interface FillQuestion {
  id: string;
  text: string;
  type: string;
  options?: string[];
  required?: boolean;
}

interface FillRunnerProps {
  poll: { id: string; name?: string; synonym?: string };
  values: Record<string, unknown>;
  setValues: (fn: (prev: Record<string, unknown>) => Record<string, unknown>) => void;
  busy?: boolean;
  /** Клиент psy (объект тестирования) — показывается в шапке прохождения. */
  subjectName?: string;
  /** Готовые вопросы (публичное прохождение по ссылке — GetPollFull недоступен
   *  без входа; форма приходит в ResolvePollGuestLink). Пусто — загрузка RPC. */
  questions?: FillQuestion[];
  onCancel: () => void;
  onSubmit: () => void;
}

type QuestionsState = 'loading' | 'ready' | 'error';

export function FillRunner({ poll, values, setValues, busy, subjectName, questions: providedQuestions, onCancel, onSubmit }: FillRunnerProps) {
    const [questions, setQuestions] = useState<FillQuestion[]>([]);
    const [qState, setQState] = useState<QuestionsState>('loading');
    const [qError, setQError] = useState('');
    const [step, setStep] = useState(0);

    const loadQuestions = useCallback(async () => {
        setQState('loading');
        setQError('');
        try {
            const r: any = await getSDK().poll.getPollFull({ id: poll.id });
            const list: FillQuestion[] = Array.isArray(r?.questions) ? r.questions : [];
            setQuestions(list);
            setStep(0);
            setQState('ready');
        } catch (e) {
            // Ошибка НЕ глотается: текст — пользователю, детали — в журнал.
            logErrorToJournal(e, `Загрузка вопросов методики «${poll.synonym || poll.name || poll.id}»`);
            setQError(friendlyError(e));
            setQuestions([]);
            setQState('error');
        }
    }, [poll.id, poll.synonym, poll.name]);

    useEffect(() => {
        if (providedQuestions && providedQuestions.length > 0) {
            setQuestions(providedQuestions);
            setQState('ready');
            setQError('');
            setStep(0);
            return;
        }
        void loadQuestions();
    }, [loadQuestions, providedQuestions]);

    const setV = (qid: string, v: unknown) => setValues((prev) => ({ ...prev, [qid]: v }));
    const total = questions.length;
    const q = questions[step];
    const isLast = step >= total - 1;
    const next = () => { if (!isLast) setStep((s) => s + 1); };
    const back = () => { if (step > 0) setStep((s) => s - 1); };
    const canNext = () => {
        if (!q || !q.required) return true;
        const v = values[q.id];
        if (q.type === 'multi') return Array.isArray(v) && v.length > 0;
        return v !== undefined && v !== '' && v !== null;
    };
    const pickSingle = (qid: string, o: string) => {
        setV(qid, o);
        setTimeout(() => { if (!isLast) setStep((s) => Math.min(s + 1, total - 1)); }, 180);
    };

    const title = poll.synonym || poll.name || 'Методика';
    const showForm = qState === 'ready' && total > 0;

    return (
        <div className="fixed inset-0 z-[12000] flex items-stretch justify-center bg-black/50 p-0 sm:items-center sm:p-4" onMouseDown={onCancel}>
            <div className="flex h-full w-full flex-col bg-card shadow-2xl sm:h-auto sm:max-h-[88vh] sm:w-[min(680px,94vw)] sm:rounded-xl" onMouseDown={(e: any) => e.stopPropagation()}>
                <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:pt-3">
                    <span className="font-medium text-sm truncate">{title}</span>
                    {subjectName && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary truncate max-w-[40%]" title={subjectName}>
                            Клиент: {subjectName}
                        </span>
                    )}
                    <span className="ml-auto text-[10px] text-muted-foreground whitespace-nowrap">
                        {showForm ? `${Math.min(step + 1, total)} / ${total}` : ''}
                    </span>
                </div>
                <div className="h-1 shrink-0 bg-muted">
                    {showForm && <div className="h-full bg-primary transition-all duration-300" style={{ width: `${Math.round((step / total) * 100)}%` }} />}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
                    {qState === 'loading' && (
                        <div className="text-xs text-muted-foreground">Загрузка вопросов…</div>
                    )}
                    {qState === 'error' && (
                        <div className="space-y-3">
                            <div className="text-sm text-destructive">Не удалось загрузить вопросы методики.</div>
                            <div className="text-xs text-muted-foreground break-words">{qError}</div>
                            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => void loadQuestions()}>Повторить</Button>
                        </div>
                    )}
                    {qState === 'ready' && total === 0 && (
                        <div className="space-y-2">
                            <div className="text-sm font-medium">В методике нет вопросов</div>
                            <div className="text-xs text-muted-foreground">
                                Это внешняя методика: тексты вопросов и ключи не включены в поставку (требуется лицензия).
                                Результат вносится вручную в протокол — дата, баллы и интерпретация заполняются в карточке протокола.
                            </div>
                        </div>
                    )}
                    {showForm && q && (
                        <div className="space-y-3">
                            <div className="text-base font-medium">{q.text}{q.required && <span className="text-destructive"> *</span>}</div>
                            {q.type === 'single' && (
                                <div className="space-y-1.5">
                                    {(q.options || []).map((o: string) => (
                                        <button key={o} onClick={() => pickSingle(q.id, o)}
                                            className={`min-h-11 w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${values[q.id] === o ? 'border-primary bg-primary/10' : 'hover:bg-muted/50'}`}>{o}</button>
                                    ))}
                                </div>
                            )}
                            {q.type === 'multi' && (
                                <div className="space-y-1.5">
                                    {(q.options || []).map((o: string) => {
                                        const arr: string[] = (values[q.id] as string[]) || [];
                                        const on = arr.includes(o);
                                        return (
                                            <button key={o} onClick={() => setV(q.id, on ? arr.filter((x: string) => x !== o) : [...arr, o])}
                                                className={`min-h-11 w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${on ? 'border-primary bg-primary/10' : 'hover:bg-muted/50'}`}>{on ? '☑' : '☐'} {o}</button>
                                        );
                                    })}
                                </div>
                            )}
                            {q.type === 'scale1_5' && (
                                <div className="grid grid-cols-5 gap-1.5 sm:flex sm:gap-2">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button key={n} onClick={() => { setV(q.id, n); setTimeout(() => { if (!isLast) setStep((s) => s + 1); }, 180); }}
                                            className={`h-12 w-full rounded-lg border text-sm font-medium transition-colors sm:h-11 sm:w-11 ${Number(values[q.id]) === n ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted/50'}`}>{n}</button>
                                    ))}
                                </div>
                            )}
                            {q.type === 'number' && (
                                <input autoFocus type="number" className="w-full h-10 text-sm border rounded-lg bg-background px-3"
                                    value={(values[q.id] as string) ?? ''} onChange={(e) => setV(q.id, e.target.value)}
                                    onKeyDown={(e: any) => e.key === 'Enter' && next()} />
                            )}
                            {q.type === 'text' && (
                                <textarea autoFocus rows={3} className="w-full text-sm border rounded-lg bg-background px-3 py-2"
                                    value={(values[q.id] as string) || ''} onChange={(e) => setV(q.id, e.target.value)} />
                            )}
                            {q.type === 'date' && (
                                <input autoFocus type="date" className="w-full h-10 text-sm border rounded-lg bg-background px-3"
                                    value={(values[q.id] as string) || ''} onChange={(e) => setV(q.id, e.target.value)} />
                            )}
                        </div>
                    )}
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2 border-t px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-3">
                    {showForm ? (
                        <>
                            <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={step === 0} onClick={back}>← Назад</Button>
                            <span className="text-[10px] text-muted-foreground">Назад сохраняет ответ</span>
                            <div className="ml-auto flex gap-2">
                                <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={onCancel}>Отмена</Button>
                                {isLast
                                    ? <Button size="sm" className="h-7 text-[11px]" disabled={busy} onClick={onSubmit}>Отправить</Button>
                                    : <Button size="sm" className="h-7 text-[11px]" disabled={!canNext()} onClick={next}>Далее →</Button>}
                            </div>
                        </>
                    ) : (
                        <div className="ml-auto">
                            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onCancel}>Закрыть</Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
